import { css } from '@linaria/core'
import { type FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ProductPath } from '#app/types/AppTypes'
import { AsideComponent } from '#components/AsideComponent'
import { Select, type SelectEntry } from '#components/Select'
import { SidebarSearch } from '#components/SidebarSearch'
import { style } from '#styles'
import type { FilterState } from '../Details/Filter/FilterState'
import type { ComparisonDisplayMode, ComparisonTableContext } from './ComparisonTableContext'
import { ComparisonTree } from './ComparisonTree'
import type { ConfigurationDiff } from './useConfigurationDifference'
import type { ComparisonDataEntry } from './useLinearizeComparisonData'

const comparisonSidebarClass = css`
  width: 250px;

  > ._group {
    position: sticky;
    top: 48px;
    background: ${style.themeColors.backgroundDefault};
    z-index: 1;
  }

  > input._search,
  > ._group {
    width: 100%;    
  }

  > ._group {
    padding: 16px 16px 8px 24px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    > ._selection {
      flex: 1;
      > ._button {
        padding: 7px 7px;
        flex: 1;
      }
    }
  }
`

const useComparisonOptions = () => {
  const { t } = useTranslation()

  return useMemo(
    () =>
      [
        {
          id: 'differences',
          value: t('configuration.comparison.sidebar.differences', 'Differences'),
        },
        {
          id: 'differences-no-missing',
          value: t('configuration.comparison.sidebar.differences-no-missing', 'Differences (without missing)'),
        },
        {
          id: 'price-differences',
          value: t('configuration.comparison.sidebar.priceDifferences', 'Price Differences'),
        },
        {
          id: 'selected',
          value: t('configuration.comparison.sidebar.selected', 'Selected'),
        },
        {
          id: 'all',
          value: t('configuration.comparison.sidebar.all', 'All'),
        },
      ] satisfies SelectEntry<ComparisonDisplayMode>[],
    [t],
  )
}

export const ComparisonSidebar: FC<{
  displayedEntries: ComparisonDataEntry[]
  displayMode: ComparisonDisplayMode
  setDisplayMode: (mode: ComparisonDisplayMode) => unknown
  filterState: FilterState
  setFilterState: (state: FilterState) => unknown
  diff: ConfigurationDiff | undefined
  selectedEntries: Partial<Record<ProductPath, boolean>>
  scrollTo: ComparisonTableContext['virtualizer']['scrollTo']
  beforeSearch?: React.ReactNode
  afterSearch?: React.ReactNode
  afterSelect?: React.ReactNode
  children?: React.ReactNode
}> = ({
  displayedEntries,
  displayMode,
  setDisplayMode,
  filterState,
  setFilterState,
  diff,
  selectedEntries,
  scrollTo,
  beforeSearch,
  afterSelect,
  afterSearch,
  children,
}) => {
  const options = useComparisonOptions()

  return (
    <AsideComponent side="left" width={250} viewClassName={comparisonSidebarClass}>
      {beforeSearch}

      <SidebarSearch filterState={filterState} onFilterChange={setFilterState} />

      {afterSearch}

      {afterSearch}

      <div className="_group">
        <Select className="_selection" options={options} onChange={(id) => setDisplayMode(id ?? 'differences')} slim value={displayMode} />
      </div>

      {afterSelect}

      <ComparisonTree
        displayedEntries={displayedEntries}
        displayMode={displayMode}
        diff={diff}
        selectedEntries={selectedEntries}
        scrollTo={scrollTo}
      />

      {children}
    </AsideComponent>
  )
}
